/**
 * The questions Praxis asked, and what the card does with them.
 *
 * Every write answers with the whole store, so each one puts that answer in
 * the cache rather than reading again. The progress stream says when a queue
 * moves, which is how an ask's questions arrive. An answer can confirm an
 * element or add one, so a write that answers reads the invention again.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  QuestionAnswer,
  QuestionStoreResponse,
} from "@patent-studio/api-types";
import {
  answerQuestion,
  askAt,
  clearQueue,
  prepareAt,
  readQuestions,
  saveReply,
  skipQuestion,
  submitAnswers,
  type QuestionTrigger,
} from "@/services/questions";
import {
  generateReviewKey,
  inventionElementsKey,
  questionsKey,
  thesisGateScopes,
} from "./query-keys";

/** The task's questions and queues. */
export function useQuestions(taskId: string) {
  return useQuery({
    queryKey: questionsKey(taskId),
    queryFn: () => readQuestions(taskId),
    enabled: taskId !== "",
  });
}

/** The card's writes, each landing the store it answers with. */
export function useQuestionWrites(taskId: string) {
  const client = useQueryClient();
  const land = (store: QuestionStoreResponse) => {
    client.setQueryData(questionsKey(taskId), store);
  };
  const answered = (store: QuestionStoreResponse) => {
    land(store);
    void client.invalidateQueries({ queryKey: inventionElementsKey(taskId) });
    for (const queryKey of thesisGateScopes(taskId)) {
      void client.invalidateQueries({ queryKey });
    }
  };
  return {
    ask: useMutation({
      mutationFn: (trigger: QuestionTrigger) => askAt(taskId, trigger),
      onSuccess: land,
    }),
    // Nothing to land: the press finds what it asked. A refusal costs the attorney nothing.
    prepare: useMutation({
      mutationFn: (trigger: QuestionTrigger) => prepareAt(taskId, trigger),
    }),
    reply: useMutation({
      mutationFn: (given: { questionId: string; draft: QuestionAnswer }) =>
        saveReply(taskId, given.questionId, given.draft),
      onSuccess: land,
    }),
    skip: useMutation({
      mutationFn: (questionId: string) => skipQuestion(taskId, questionId),
      onSuccess: land,
    }),
    // An answer moves the open items the review serves, so the review is read again.
    submit: useMutation({
      mutationFn: (questionIds: readonly string[]) =>
        submitAnswers(taskId, questionIds),
      onSuccess: (store) => {
        answered(store);
        void client.invalidateQueries({ queryKey: generateReviewKey(taskId) });
      },
    }),
    // A commit card's Reply: one request that answers, so leaving the page cannot stop it
    // half way; the card's others are assessed with it at its clear.
    answer: useMutation({
      mutationFn: (given: { questionId: string; answer: QuestionAnswer }) =>
        answerQuestion(taskId, given.questionId, given.answer),
      onSuccess: answered,
    }),
    clear: useMutation({
      mutationFn: (given: { queueId: string; replied: readonly string[] }) =>
        clearQueue(taskId, given.queueId, given.replied),
      onSuccess: answered,
    }),
  };
}
